import { useMemo } from 'react';

import { EntityBadgeByIds } from '@/lib/crm/components/badges';
import type {
  DraftEntity,
  DraftParentInfo,
} from '@/lib/crm/core/draftEntities';
import type { AssociationType } from '@/lib/crm/core/SchemaClient';

import { AssociationInput } from '../associations/AssociationInput';
import { LabelWithIndicator } from './LabelWithIndicator';

export interface CrmAssociationFieldProps {
  /** Association type schema */
  associationType: AssociationType;
  /** Current association value - entity_id(s) */
  value: string | string[] | null | undefined;
  /** Callback when value changes */
  onChange: (value: string | string[] | null) => void;
  /** Org ID for fetching association options */
  orgId: number;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** When true, renders value as badges instead of a select input */
  readOnly?: boolean;
  /** Whether the field has been edited (shows blue dot) */
  isEdited?: boolean;
  /** Error message to display */
  error?: string;
  /** Source object's entity_id - used to prepopulate reverse association when creating */
  sourceEntityId?: string;
  /** Optional guard callback before opening create modal. Call proceed() to continue. */
  onBeforeCreate?: (proceed: () => void) => void;
  /** Whether a saved value exists (for *_to_one, hides create option if true) */
  hasSavedValue?: boolean;
  /** Set of entity type names that are stage types (have "stage" in entity_kind) */
  stageTypeNames?: Set<string>;
  /** When true, conflict-bearing entities remain selectable; conflicts surface via ConflictResolutionPanel. */
  allowOverwrite?: boolean;
  /**
   * Draft-mode create handler — when set, the entity picker's "Create new"
   * flow builds a client-side DraftEntity and reports it here instead of
   * eagerly creating it on the server. Forwarded to AssociationInput.
   */
  onCreateDraft?: (draft: DraftEntity) => void;
  /** Hide the "Create new …" option entirely (e.g. in filter contexts). Forwarded to AssociationInput. */
  hideCreateOption?: boolean;
  /**
   * Parent-identity context threaded down from a parent draft form so a
   * nested create can render a "belongs to {parent}" banner. Forwarded to
   * AssociationInput.
   */
  draftParentInfo?: DraftParentInfo;
}

/**
 * CrmAssociationField - Wrapper component that renders a label + AssociationInput + error.
 *
 * This component composes:
 * - LabelWithIndicator: Shows label, required asterisk, tooltip, and edit indicator
 * - AssociationInput: The actual association selector based on cardinality (edit mode)
 * - EntityBadgeByIds: Read-only badge display of associated objects (read-only mode)
 * - Error display: Shows error message below the input
 *
 * Use this for form contexts where labels are needed.
 * Use AssociationInput directly for filter contexts where labels aren't needed.
 *
 * @example
 * // Edit mode (default)
 * <CrmAssociationField
 *   associationType={companyAssocType}
 *   value={companyId}
 *   onChange={setCompanyId}
 *   orgId={orgId}
 *   isEdited={hasChanges}
 * />
 *
 * @example
 * // Read-only mode
 * <CrmAssociationField
 *   associationType={companyAssocType}
 *   value={companyId}
 *   onChange={setCompanyId}
 *   orgId={orgId}
 *   readOnly
 * />
 */
export function CrmAssociationField({
  associationType,
  value,
  onChange,
  orgId,
  disabled = false,
  readOnly = false,
  isEdited = false,
  error,
  sourceEntityId,
  onBeforeCreate,
  hasSavedValue,
  stageTypeNames,
  allowOverwrite,
  onCreateDraft,
  hideCreateOption,
  draftParentInfo,
}: CrmAssociationFieldProps) {
  // Normalise the association value (`string | string[] | null | undefined`)
  // into a stable `string[]` so the readonly badge wrapper gets the same
  // array reference between renders.
  const entityIds = useMemo<string[]>(() => {
    if (!value) {
      return [];
    }
    return (Array.isArray(value) ? value : [value]).filter(Boolean);
  }, [value]);

  return (
    <div className='flex flex-col gap-1.5'>
      <LabelWithIndicator
        label={associationType.label}
        isEdited={!readOnly && isEdited}
      />
      {readOnly && entityIds.length === 0 && (
        <span className='text-sm text-tertiary'>—</span>
      )}
      {readOnly && entityIds.length > 0 && (
        <EntityBadgeByIds
          entityIds={entityIds}
          orgId={orgId}
          maxVisible={entityIds.length}
        />
      )}
      {!readOnly && (
        <AssociationInput
          associationType={associationType}
          value={value}
          onChange={onChange}
          orgId={orgId}
          disabled={disabled}
          sourceEntityId={sourceEntityId}
          onBeforeCreate={onBeforeCreate}
          hasSavedValue={hasSavedValue}
          stageTypeNames={stageTypeNames}
          allowOverwrite={allowOverwrite}
          onCreateDraft={onCreateDraft}
          hideCreateOption={hideCreateOption}
          draftParentInfo={draftParentInfo}
        />
      )}
      {!readOnly && error && (
        <span className='text-sm text-utility-error-600'>{error}</span>
      )}
    </div>
  );
}
